import {
  patientPhotoUrlResponseSchema,
  type PatientPhotoMimeType,
  type PatientPhotoUrlResponse,
} from '@emr/shared';

import { ApiError, apiRequest } from './client.ts';
import { fileToBase64 } from '../lib/fileToBase64.ts';

/**
 * Patient photo on the chart profile (`BACKLOG.md` C18) — validated against `@emr/shared`'s own
 * response schema, matching `api/patients.ts`'s own reasoning for why every client here goes
 * through the shared schema rather than a locally re-derived approximation of it.
 */

/** `GET /api/patients/:patientId/photo` — `patient:read`. Resolves to `null` on a 404, which this
 * route uses both for "no such patient" and "this patient has no photo set yet" — the caller
 * (`useQuery`, in practice) has no reason to tell those two apart, since both render the same "no
 * photo" state. Any other failure (network, a genuine contract mismatch) still throws, matching
 * every other reader in `api/`. */
export async function fetchPatientPhotoUrl(
  patientId: string,
  signal?: AbortSignal,
): Promise<string | null> {
  try {
    const response = await apiRequest(
      `/api/patients/${patientId}/photo`,
      patientPhotoUrlResponseSchema,
      signal === undefined ? {} : { signal },
    );
    return response.url;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/** `PUT /api/patients/:patientId/photo` — `patient:write`. Replaces the patient's current photo, if
 * any. `declaredMimeType` is the caller's own responsibility to supply correctly — the server
 * verifies it against the real bytes regardless (`documentVerification.ts`'s `verifyMagicBytes`) and
 * refuses a mismatch with a `document_content_mismatch` `ApiError`, which this function does not
 * catch or translate; the caller decides how to surface that. */
export async function uploadPatientPhoto(
  patientId: string,
  file: File,
  declaredMimeType: PatientPhotoMimeType,
): Promise<PatientPhotoUrlResponse> {
  const contentBase64 = await fileToBase64(file);
  return apiRequest(`/api/patients/${patientId}/photo`, patientPhotoUrlResponseSchema, {
    method: 'PUT',
    body: { filename: file.name, declaredMimeType, contentBase64 },
  });
}
